<script setup lang="ts">
import {RouteLocationRaw} from "vue-router";
import IconEmptyList from "./icons/IconEmptyList.vue";
import {ref} from "vue";
import BasicTabLink from "./basic/BasicTabLink.vue";
import {type Component} from 'vue';

export type BasicCardTab = {
  key: string,
  title: string;
  node: Component;
}

type IProps = {
  title?: string;
  sidebarTitle?: string;
  showAll?: RouteLocationRaw | null;
  highlighted?: boolean,
  tabs?: BasicCardTab[],
  collapsible?: boolean,
};
const props = withDefaults(defineProps<IProps>(), {
  showAll: null,
  highlighted: false,
  collapsible: false,
});

const activeTab = ref<BasicCardTab | null>(props.tabs ? props.tabs[0] : null);
const isCollapsed = ref(false);

function changeTab(key: string) {
  if (!props.tabs) {
    return;
  }

  for (const tab of props.tabs) {
    if (tab.key === key) {
      activeTab.value = tab;
      return;
    }
  }
}

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}
</script>

<template>
  <div class="revolut-card-wrapper">
    <div
        class="h-full flex flex-col gap-6 p-8"
        :class="{'w-full': !$slots.sidebar, 'w-2/3': $slots.sidebar}"
    >
      <div class="w-full flex justify-between items-center">
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2">
            <h2 v-if="props.title" class="font-semibold text-2xl dark:text-white text-gray-900">{{ props.title }}</h2>
            <button
              v-if="props.collapsible"
              @click="toggleCollapse"
              class="transition-transform duration-200 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              :class="{ 'rotate-180': isCollapsed }"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>
          <slot name="titleWidget"></slot>
          <div v-if="props.tabs && activeTab" class="flex items-center gap-3">
            <BasicTabLink
                v-for="tab in props.tabs"
                :key="tab.key"
                :class="{active: tab.key === activeTab.key}"
                @click="changeTab(tab.key)"
            >
              {{ tab.title.toUpperCase() }}
            </BasicTabLink>
          </div>
        </div>
        <slot name="endWidget"></slot>
        <RouterLink
            v-if="props.showAll !== null"
            class="font-medium text-base text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            :to="props.showAll"
        >
          {{ $t("Show all") }}
        </RouterLink>
      </div>

      <div v-if="!isCollapsed">
        <component v-if="activeTab" :is="activeTab?.node"/>
        <slot v-else>
          <div class="flex justify-center w-full h-full">
            <IconEmptyList/>
          </div>
        </slot>
      </div>
    </div>
    <div class="w-1/3 flex flex-col gap-6 bg-gray-50 dark:bg-gray-800/50 p-8 rounded-r-2xl" v-if="$slots.sidebar">
      <h2 v-if="props.sidebarTitle" class="font-semibold text-2xl dark:text-white text-gray-900">{{ props.sidebarTitle }}</h2>
      <slot name="sidebar">

      </slot>
      <div class="flex justify-center w-full h-full">
        <IconEmptyList/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.revolut-card-wrapper {
  @apply w-full h-full rounded-3xl flex backdrop-blur-xl border;
  background: rgba(17, 24, 39, 0.4); /* bg-gray-900/40 */
  border-color: rgba(31, 41, 55, 0.3); /* border-gray-800/30 */
  transition: all 0.3s ease;

  &:hover {
    background: rgba(17, 24, 39, 0.5);
    border-color: rgba(31, 41, 55, 0.5);
  }
}
</style>
